import { useId, useState } from 'react'
import Field from './Field.jsx'
import InlineAlert from './InlineAlert.jsx'
import Toggle from './Toggle.jsx'
import { ApiError } from '../lib/api.js'

/**
 * A title and a private switch — the whole of a new list (Q19).
 *
 * One form in two places: revealed on your own profile's Lists tab, where it
 * makes a list and opens it, and inside *Add to a list*, where it makes one
 * and adds to it. What happens after the create is the caller's `onSubmit`;
 * what happens when that throws is this form's, so both places refuse the
 * same way — the typed title and the switch stay where they were, and the
 * sentence is the API's own, or the list page's fallback when nothing
 * answered.
 *
 * No client-side wall for an unverified address. The server refuses a public
 * list with its own sentence, exactly as it refuses a review, and this form
 * shows that sentence rather than predicting it from `email_verified`.
 */
export default function NewListForm({ submitLabel, onSubmit, onCancel, disabled = false }) {
  const helpId = useId()
  const [title, setTitle] = useState('')
  const [visibility, setVisibility] = useState('public')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const busy = saving || disabled

  async function submit(event) {
    event.preventDefault()
    const trimmed = title.trim()
    if (!trimmed || busy) return
    setError('')
    setSaving(true)
    try {
      await onSubmit({ title: trimmed, visibility })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that just now.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="list-form" onSubmit={submit}>
      <Field
        label="Title"
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        maxLength={140}
        readOnly={busy}
        autoFocus
      />
      <div className="form-row p-0">
        <div className="row-main">
          <div className="row-title">Keep this list private</div>
          <div className="row-sub wrap" id={helpId}>
            Only you can see it.
          </div>
        </div>
        <Toggle
          checked={visibility === 'private'}
          onChange={(next) => setVisibility(next ? 'private' : 'public')}
          label="Keep this list private"
          describedBy={helpId}
          disabled={busy}
        />
      </div>
      <InlineAlert>{error}</InlineAlert>
      <div className="list-form-actions">
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !title.trim()}>
          {saving ? 'Saving…' : submitLabel}
        </button>
        {onCancel ? (
          <button type="button" className="btn btn-quiet btn-sm" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  )
}
